import { useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Lightbulb, X } from "@phosphor-icons/react";
import {
  selectClinicId,
  selectClinicProfile,
  selectDaysSinceRegistration,
  selectTherapistProfile,
  setTherapistProfile,
} from "../../store/slices/authSlice";
import {
  useGetFeatureTipsQuery,
  useDismissFeatureTipMutation,
} from "../../store/services/therapistApi";
import { NUDGE_CATALOG, isNudgeSuppressed } from "../../config/nudgeCatalog";
import "./FeatureTipsCard.css";

const NINETY_DAYS_MS = 90 * 24 * 60 * 60 * 1000;

// dismissedTips values may be a Firestore Timestamp, a serialized
// { seconds } object, an ISO string, or a Date — parse defensively.
const toMillis = (value) => {
  if (!value) return null;
  try {
    if (typeof value.toDate === "function") return value.toDate().getTime();
    if (typeof value.seconds === "number") return value.seconds * 1000;
    if (value instanceof Date) return value.getTime();
    if (typeof value === "string" || typeof value === "number") {
      const ms = new Date(value).getTime();
      return Number.isNaN(ms) ? null : ms;
    }
  } catch {
    return null;
  }
  return null;
};

// Two shapes co-exist: a bare timestamp written before permanent-kill existed,
// and the current { at, killed } object. The `value.at === undefined` guard is
// load-bearing — a Redux-serialized legacy Timestamp is also `{ seconds }`.
const parseDismissal = (value) => {
  if (!value) return null;
  const isLegacy =
    typeof value.toDate === "function" ||
    value instanceof Date ||
    typeof value === "string" ||
    typeof value === "number" ||
    (typeof value.seconds === "number" && value.at === undefined);
  if (isLegacy) return { at: toMillis(value), killed: false };
  return { at: toMillis(value.at), killed: value.killed === true };
};

const isDismissed = (dismissedTips, featureKey) => {
  const dismissal = parseDismissal(dismissedTips?.[featureKey]);
  if (!dismissal) return false;
  if (dismissal.killed) return true;
  if (dismissal.at == null) return false;
  return Date.now() - dismissal.at < NINETY_DAYS_MS;
};

const FeatureTipsCard = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const clinicId = useSelector(selectClinicId);
  const clinicProfile = useSelector(selectClinicProfile);
  const therapistProfile = useSelector(selectTherapistProfile);
  const daysSinceRegistration = useSelector(selectDaysSinceRegistration);
  const [hiddenKeys, setHiddenKeys] = useState([]);

  const { data: tipsDoc, isError } = useGetFeatureTipsQuery(clinicId, {
    skip: !clinicId,
  });
  const [dismissFeatureTip] = useDismissFeatureTipMutation();

  const tipKey = useMemo(() => {
    if (!tipsDoc) return null;
    const dismissedTips = therapistProfile?.dismissedTips;
    const visibleMap = tipsDoc.visible || {};
    const isShowable = (featureKey) =>
      NUDGE_CATALOG[featureKey] &&
      !hiddenKeys.includes(featureKey) &&
      !isDismissed(dismissedTips, featureKey) &&
      !isNudgeSuppressed(featureKey, { clinicProfile, therapistProfile });

    const nudges = tipsDoc.nudges;
    const therapistId = therapistProfile?.uid;

    // Legacy path — clinics from before the nudge engine. Nothing is ever
    // auto-shown there: a NiV admin opts each tip in via the `visible` map.
    if (!nudges?.autoEnabled || !therapistId) {
      if (!tipsDoc.tips?.length) return null;
      return (
        [...tipsDoc.tips]
          .sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
          .map((t) => t.featureKey)
          .find((key) => visibleMap[key] === true && isShowable(key)) || null
      );
    }

    // Auto path. The nightly job decided eligibility; `visible` is now an
    // override in either direction, and a force-show still honors dismissal.
    const mine = nudges.byTherapist?.[therapistId] || {};
    const minDays = nudges.minDays || {};
    const forcedKeys = Object.keys(visibleMap).filter(
      (key) => visibleMap[key] === true
    );
    const autoKeys = (mine.order || []).filter(
      (key) =>
        visibleMap[key] !== false &&
        mine.eligible?.[key] === true &&
        // Re-check the day threshold locally: the server computed it up to 24h
        // ago, so a borderline tip must not appear a day early.
        (minDays[key] == null ||
          (daysSinceRegistration != null &&
            daysSinceRegistration >= minDays[key]))
    );

    return [...forcedKeys, ...autoKeys].find(isShowable) || null;
  }, [
    tipsDoc,
    therapistProfile,
    clinicProfile,
    daysSinceRegistration,
    hiddenKeys,
  ]);

  if (isError || !tipKey) return null;

  const content = NUDGE_CATALOG[tipKey];
  const { Icon } = content;

  const handleDismiss = (permanent) => {
    setHiddenKeys((prev) => [...prev, tipKey]);
    const uid = therapistProfile?.uid;
    if (!uid) return;
    dismissFeatureTip({ uid, featureKey: tipKey, permanent });
    // The therapist profile has no live listener, so without this optimistic
    // patch the dismissal would only take effect after the next login.
    dispatch(
      setTherapistProfile({
        ...therapistProfile,
        dismissedTips: {
          ...(therapistProfile.dismissedTips || {}),
          [tipKey]: { at: new Date().toISOString(), killed: permanent },
        },
      })
    );
  };

  return (
    <div className="ftc-card">
      <button
        type="button"
        className="ftc-dismiss"
        onClick={() => handleDismiss(false)}
        aria-label="סגירת הטיפ"
      >
        <X size={14} weight="bold" />
      </button>
      <div className="ftc-icon-wrap">
        <Icon size={20} weight="fill" />
      </div>
      <div className="ftc-body">
        <div className="ftc-label">
          <Lightbulb size={11} weight="fill" />
          <span>טיפ בשבילכם</span>
        </div>
        <h3 className="ftc-title">{content.title}</h3>
        <p className="ftc-text">{content.body}</p>
        <button
          type="button"
          className="ftc-never"
          onClick={() => handleDismiss(true)}
        >
          לא להציג לי את זה שוב
        </button>
      </div>
      <button
        type="button"
        className="ftc-cta"
        onClick={() => navigate(content.route)}
      >
        גלו עוד
      </button>
    </div>
  );
};

export default FeatureTipsCard;
